import { useEffect, useState } from "react";
import api from "@/lib/api";

export default function AdminShipping() {
    const [zones, setZones] = useState([]);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/content/shipping.zones");
            setZones(Array.isArray(data.value) ? data.value : []);
        } catch {
            setZones([]);
        }
    };
    useEffect(() => { load(); }, []);

    const update = (idx, patch) => setZones((zs) => zs.map((z, i) => i === idx ? { ...z, ...patch } : z));
    const updateMethod = (zi, mi, patch) => setZones((zs) => zs.map((z, i) => i === zi ? { ...z, methods: z.methods.map((m, j) => j === mi ? { ...m, ...patch } : m) } : z));
    const addMethod = (zi) => update(zi, { methods: [...(zones[zi].methods || []), { id: `method_${Date.now()}`, name: "New method", rate: 20, free_over: null }] });
    const removeMethod = (zi, mi) => update(zi, { methods: zones[zi].methods.filter((_, j) => j !== mi) });
    const addZone = () => setZones([...zones, { id: `zone_${Date.now()}`, name: "New zone", countries: [], methods: [{ id: "standard", name: "Standard", rate: 20, free_over: null }] }]);
    const removeZone = (zi) => { if (window.confirm("Delete this zone?")) setZones(zones.filter((_, i) => i !== zi)); };
    const moveZone = (zi, dir) => {
        const j = zi + dir;
        if (j < 0 || j >= zones.length) return;
        const arr = [...zones];
        [arr[zi], arr[j]] = [arr[j], arr[zi]];
        setZones(arr);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/content/shipping.zones", { value: zones });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } finally {
            setSaving(false);
        }
    };

    const resetDefault = async () => {
        if (!window.confirm("Reset all shipping zones to the defaults?")) return;
        const { data } = await api.post("/content/shipping.zones/reset");
        setZones(Array.isArray(data.value) ? data.value : []);
    };

    return (
        <div data-testid="admin-shipping">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                <div>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>Shipping</div>
                    <p style={{ margin: 0, fontFamily: "var(--font-serif)", fontStyle: "italic", color: "var(--cocoa-soft)" }}>
                        Zones checked top to bottom. Empty country list = catch-all (put Rest of World last).
                    </p>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <button className="btn btn-outline" onClick={resetDefault}>Reset defaults</button>
                    <button className="btn" onClick={save} disabled={saving} data-testid="admin-shipping-save">
                        {saving ? "Saving…" : saved ? "Saved ✓" : "Save all zones"}
                    </button>
                </div>
            </div>

            {zones.map((z, zi) => (
                <div key={zi} style={{ border: "1px solid var(--sand-line)", padding: 20, marginBottom: 16, background: "var(--parchment-soft)" }} data-testid={`shipping-zone-${zi}`}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "start", marginBottom: 16 }}>
                        <div className="field" style={{ margin: 0 }}>
                            <label className="label">Zone name</label>
                            <input className="input" value={z.name || ""} onChange={(e) => update(zi, { name: e.target.value })} />
                        </div>
                        <div className="field" style={{ margin: 0 }}>
                            <label className="label">Countries (ISO codes, comma-separated · leave blank = catch-all)</label>
                            <input
                                className="input"
                                value={(z.countries || []).join(", ")}
                                onChange={(e) => update(zi, { countries: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })}
                                placeholder="e.g. FR, DE, NL"
                                data-testid={`shipping-zone-${zi}-countries`}
                            />
                        </div>
                        <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: "100%" }}>
                            <button onClick={() => moveZone(zi, -1)} disabled={zi === 0} style={{ padding: "10px 12px", background: "transparent", border: "1px solid var(--sand)", cursor: zi === 0 ? "not-allowed" : "pointer", opacity: zi === 0 ? 0.4 : 1 }}>▲</button>
                            <button onClick={() => moveZone(zi, 1)} disabled={zi === zones.length - 1} style={{ padding: "10px 12px", background: "transparent", border: "1px solid var(--sand)", cursor: zi === zones.length - 1 ? "not-allowed" : "pointer", opacity: zi === zones.length - 1 ? 0.4 : 1 }}>▼</button>
                            <button onClick={() => removeZone(zi)} style={{ padding: "10px 12px", background: "transparent", border: "1px solid #7a2f22", color: "#7a2f22", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase" }}>Delete</button>
                        </div>
                    </div>

                    <div className="eyebrow" style={{ marginBottom: 8 }}>Methods</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "var(--font-serif)" }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: "left", padding: "8px 12px 8px 0", fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--sand)" }}>Method name</th>
                                <th style={{ textAlign: "left", padding: "8px 12px", fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--sand)", width: 120 }}>Rate (£)</th>
                                <th style={{ textAlign: "left", padding: "8px 12px", fontFamily: "var(--font-sans)", fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--sand)", width: 140 }}>Free over (£)</th>
                                <th style={{ width: 40 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {(z.methods || []).map((m, mi) => (
                                <tr key={mi}>
                                    <td style={{ padding: "6px 12px 6px 0" }}>
                                        <input className="input" value={m.name || ""} onChange={(e) => updateMethod(zi, mi, { name: e.target.value })} data-testid={`shipping-method-${zi}-${mi}-name`} />
                                    </td>
                                    <td style={{ padding: "6px 12px" }}>
                                        <input className="input" type="number" step="0.01" value={m.rate ?? 0} onChange={(e) => updateMethod(zi, mi, { rate: parseFloat(e.target.value) || 0 })} data-testid={`shipping-method-${zi}-${mi}-rate`} />
                                    </td>
                                    <td style={{ padding: "6px 12px" }}>
                                        <input className="input" type="number" placeholder="blank = never" value={m.free_over ?? ""} onChange={(e) => updateMethod(zi, mi, { free_over: e.target.value === "" ? null : parseFloat(e.target.value) })} />
                                    </td>
                                    <td>
                                        <button onClick={() => removeMethod(zi, mi)} style={{ background: "transparent", border: "none", cursor: "pointer", color: "#7a2f22", fontSize: 18 }} aria-label="Remove method">×</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <button onClick={() => addMethod(zi)} style={{ marginTop: 8, background: "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-sans)", fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: "var(--cocoa)", textDecoration: "underline" }}>
                        + Add method
                    </button>
                </div>
            ))}

            <button className="btn btn-outline" onClick={addZone} data-testid="admin-shipping-add-zone">+ Add zone</button>
        </div>
    );
}
